import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import { download, ExportButton, fileName, tableSheet } from "./excel";

const money = (value) => (value === null || value === undefined ? "-" : Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const qty = (value) => (value === null || value === undefined ? "-" : Number(value).toLocaleString("en-IN", { maximumFractionDigits: 3 }));

const VALUATION_TYPES = [
  { value: "basic", label: "Basic" },
  { value: "cost", label: "Cost / Purchase Price" },
  { value: "selling", label: "Selling Price" },
];

const CategoryClosingStockReport = () => {
  const [valuationType, setValuationType] = useState("basic");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async (type) => {
    setLoading(true);
    try {
      const data = await api.get("/api/v1/reports/closing-stock-by-category", { valuationType: type });
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(error.message || "Failed to load category-wise closing stock report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(valuationType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valuationType]);

  const rateLabel = valuationType === "selling" ? "Weighted Avg Selling Price" : valuationType === "cost" ? "Weighted Avg Cost Rate" : "Weighted Avg Rate";
  const totalQty = rows.reduce((sum, r) => sum + (Number(r.quantity) || 0), 0);
  const totalValue = rows.reduce((sum, r) => sum + (Number(r.totalNetValue) || 0), 0);

  const exportExcel = () => {
    const valuation = VALUATION_TYPES.find((t) => t.value === valuationType).label;
    const columns = [
      { key: "category", label: "Category" },
      { key: "itemCount", label: "Items" },
      { key: "quantity", label: "Qty", format: "qty" },
      { key: "weightedAvgRate", label: rateLabel, format: "money", total: false },
      { key: "totalNetValue", label: "Total Net Value", format: "money" },
    ];
    download(fileName("Category Wise Closing Stock", valuation), [
      tableSheet("Category Wise Closing Stock", `Valuation: ${valuation}`, columns, rows, "Category Closing Stock"),
    ]);
  };

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading category-wise closing stock...</div>
          </div>
        </div>
      ) : null}

      <div className="page-subtitle" style={{ marginBottom: "12px" }}>
        Current on-hand quantity and value rolled up by product category, valued at the selected price basis. Rate shown
        is a quantity-weighted average across every product in the category.
      </div>

      <div className="filters-bar">
        <select value={valuationType} onChange={(e) => setValuationType(e.target.value)} aria-label="Valuation type">
          {VALUATION_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        <button className="primary" onClick={() => load(valuationType)} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
        <ExportButton onClick={exportExcel} disabled={loading || rows.length === 0} />
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Category</th>
              <th>Items</th>
              <th>Qty</th>
              <th>{rateLabel}</th>
              <th>Total Net Value</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <div className="empty-state">
                    <span className="empty-state-title">No stock on hand</span>
                  </div>
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.category}>
                  <td>{row.category}</td>
                  <td>{row.itemCount}</td>
                  <td>{qty(row.quantity)}</td>
                  <td>{money(row.weightedAvgRate)}</td>
                  <td>{money(row.totalNetValue)}</td>
                </tr>
              ))
            )}
          </tbody>
          {rows.length > 0 ? (
            <tfoot>
              <tr>
                <td style={{ fontWeight: 700 }}>Total</td>
                <td />
                <td style={{ fontWeight: 700 }}>{qty(totalQty)}</td>
                <td />
                <td style={{ fontWeight: 700 }}>{money(totalValue)}</td>
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
    </div>
  );
};

export default CategoryClosingStockReport;
